import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { Project } from '../models/types';
import { format } from 'date-fns';
import { blockUpdates, allowUpdates } from '../utils/updateGate';
import { autoSync } from '../services/syncService';
import '../styles/Modal.css';

interface ManualEntryModalProps {
  onClose: () => void;
  onSave: () => void;
  refreshTrigger?: number;
}

const ManualEntryModal: React.FC<ManualEntryModalProps> = ({ onClose, onSave, refreshTrigger = 0 }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [timeOutDate, setTimeOutDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [showLocation, setShowLocation] = useState(true);
  const [enableOvernight, setEnableOvernight] = useState(false);
  const [formData, setFormData] = useState({
    timeIn: format(new Date(), "HH:mm"), // Changed to HH:mm format
    timeOut: '',
    project: '',
    task: '',
    location: 'Site',
    notes: ''
  });

  // Same gate EditEntryModal holds: this is an editable form too, and a
  // service-worker reload while it is open throws away whatever was typed.
  // Gating only the edit form left the goal half-done — a manual entry is
  // usually the LONGER thing to retype, since none of it is pre-filled.
  useEffect(() => {
    blockUpdates();
    return () => { allowUpdates(); };
  }, []);

  useEffect(() => {
    loadProjects();

    // Load settings
    const storedShowLocation = localStorage.getItem('showLocationButtons');
    if (storedShowLocation !== null) {
      setShowLocation(storedShowLocation === 'true');
    }
    
    const storedEnableOvernight = localStorage.getItem('enableOvernightShifts');
    if (storedEnableOvernight !== null) {
      setEnableOvernight(storedEnableOvernight === 'true');
    }
  }, [refreshTrigger]);

  // Update timeOutDate when selectedDate changes, unless overnight is enabled and user changed it
  useEffect(() => {
    if (!enableOvernight) {
      setTimeOutDate(selectedDate);
    } else if (timeOutDate < selectedDate) {
      setTimeOutDate(selectedDate);
    }
  }, [selectedDate, enableOvernight, timeOutDate]);

  useEffect(() => {
    if (formData.project) {
      const project = projects.find(p => p.name === formData.project);
      if (project) {
        setTasks(project.tasks);
      }
    }
  }, [formData.project, projects]);

  const loadProjects = async () => {
    try {
      const allProjects = await db.projects.toArray();
      setProjects(allProjects);
      
      if (allProjects.length > 0) {
        setFormData(prev => ({ ...prev, project: allProjects[0].name }));
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddProject = async () => {
    const newProject = prompt('Enter new project name:');
    if (newProject && newProject.trim()) {
      const projectName = newProject.trim();
      try {
        // Check if project already exists
        const existingProjects = await db.projects
          .where('name')
          .equals(projectName)
          .toArray();
        
        if (existingProjects.length === 0) {
          // Add new project to database with default location
          const defaultLocation = localStorage.getItem('defaultLocation') || 'Site';
          await db.projects.add({
            name: projectName,
            tasks: [],
            location: defaultLocation
          });
          
          // Reload projects from database
          const allProjects = await db.projects.toArray();
          setProjects(allProjects);
          setFormData(prev => ({ ...prev, project: projectName }));
          
          // Notify parent component to refresh project lists elsewhere
          if (onSave) {
            onSave(); // This will trigger a refresh in parent components
          }
        } else {
          // Project already exists, just select it
          setFormData(prev => ({ ...prev, project: projectName }));
        }
      } catch (error) {
        console.error('Error adding project:', error);
        alert('Failed to add project. Please try again.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!selectedDate || !formData.timeIn) {
        alert('Date and Time In are required');
        return;
      }

      // Basic check for HH:mm format for timeIn
      if (formData.timeIn && !/^\d{2}:\d{2}$/.test(formData.timeIn)) {
          alert('Time In must be in HH:mm format.');
          return;
      }
      const timeInDate = new Date(`${selectedDate}T${formData.timeIn}:00`);
      
      let timeOutDateTime: Date | undefined = undefined;
      if (formData.timeOut) {
        if (!/^\d{2}:\d{2}$/.test(formData.timeOut)) {
            alert('Time Out must be in HH:mm format.');
            return;
        }
        // Use timeOutDate if overnight enabled, otherwise use selectedDate
        const dateToUse = enableOvernight ? timeOutDate : selectedDate;
        timeOutDateTime = new Date(`${dateToUse}T${formData.timeOut}:00`);
      }

      if (timeOutDateTime && timeOutDateTime <= timeInDate) {
        // If it's the same day, it must be later. If it's next day, it's fine.
        if (!enableOvernight || timeOutDate === selectedDate) {
           alert('Time Out must be after Time In.');
           return;
        }
      }

      // Check if project exists, if not create it
      if (formData.project) {
        const existingProjects = await db.projects
          .where('name')
          .equals(formData.project)
          .toArray();

        if (existingProjects.length === 0) {
          await db.projects.add({
            name: formData.project,
            tasks: formData.task ? [formData.task] : []
          });
        } else if (formData.task && !existingProjects[0].tasks.includes(formData.task)) {
          await db.projects.update(existingProjects[0].id!, {
            tasks: [...existingProjects[0].tasks, formData.task]
          });
        }
      }

      const userName = await db.getUserName();

      // Create time entry using the new addTimeEntry method
      await db.addTimeEntry({
        name: userName || 'Anonymous',
        timeIn: timeInDate,
        timeOut: timeOutDateTime,
        project: formData.project,
        task: formData.task || undefined,
        location: formData.location,
        notes: formData.notes || undefined
      });

      // Same reasoning as clock-out: send it now rather than waiting for
      // someone to remember the Sync button. This is the entry an employee
      // types BECAUSE they forgot to clock in, often days late, so it is the
      // one least likely to be followed by a clock-out that would sweep it up.
      // Fire-and-forget — a failed sync must never cost them what they typed,
      // and the entry stays queued locally either way.
      autoSync().catch((err) => console.error('Auto-sync after manual entry failed:', err));

      onSave();
      onClose();
    } catch (error) {
      console.error('Error adding time entry:', error);
      alert('Failed to add time entry');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Add Manual Time Entry</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="entryDate">Date:</label>
            <input
              type="date"
              id="entryDate"
              name="entryDate"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="timeIn">Time In:</label>
            <input
              type="time"
              id="timeIn"
              name="timeIn"
              value={formData.timeIn}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="timeOut">Time Out:</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {enableOvernight && (
                <input
                  type="date"
                  id="timeOutDate"
                  value={timeOutDate}
                  onChange={(e) => setTimeOutDate(e.target.value)}
                  min={selectedDate}
                  style={{ flex: 1 }}
                />
              )}
              <input
                type="time"
                id="timeOut"
                name="timeOut"
                value={formData.timeOut}
                onChange={handleChange}
                style={{ flex: enableOvernight ? 1 : 'initial', width: enableOvernight ? 'auto' : '100%' }}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="project">Project:</label>
            <div className="select-with-button">
              <select
                id="project"
                name="project"
                value={formData.project}
                onChange={handleChange}
                required
              >
                <option value="" disabled>Select a project</option>
                {projects.map(project => (
                  <option key={project.name} value={project.name}>
                    {project.name}
                  </option>
                ))}
              </select>
              <button type="button" onClick={handleAddProject} className="add-button">
                +
              </button>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="task">Task:</label>
            <select
              id="task"
              name="task"
              value={formData.task}
              onChange={handleChange}
              disabled={!formData.project}
            >
              <option value="">Select a task</option>
              {tasks.map(task => (
                <option key={task} value={task}>{task}</option>
              ))}
            </select>
            {!formData.project && (
              <p className="helper-text">Select a project first</p>
            )}
          </div>
          
          {showLocation && (
            <div className="form-group">
              <label htmlFor="location">Location:</label>
              <div className="location-toggle">
                <button
                  type="button"
                  className={formData.location === 'Site' ? 'active' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, location: 'Site' }))}
                >
                  Site
                </button>
                <button
                  type="button"
                  className={formData.location === 'Shop' ? 'active' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, location: 'Shop' }))}
                >
                  Shop
                </button>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="notes">Notes:</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
            />
          </div>
          
          <div className="modal-buttons-vertical">
            <button type="submit" className="save-btn">
              Save
            </button>
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualEntryModal;
